import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteIcon from '@mui/icons-material/Delete';
import EventLocationsManager from './EventLocationsManager';
import { RootState } from '../store/store';
import { searchCities } from '../services/apiService';
import { getAuthUserId } from '../utils/authUser';
import { buildEventsApiUrl } from '../config/api';

interface CityOption {
  city: string;
  state: string;
  stateAbbr: string;
  lat: number;
  lng: number;
}

interface EditEventForm {
  eventName: string;
  description: string;
  category: string;
  eventDateTime: string;
  eventStartDateTime: string;
  mustArriveByDateTime: string;
  tradeCutoffDate: string;
  selectedCity: CityOption | null;
  venueAddress: string;
  maxParticipants: string;
  participantInitiationEnabled: boolean;
  status: 'active' | 'draft' | 'deleted';
}

const categories = [
  'Books',
  'Electronics', 
  'Video Games',
  'Tabletop Games',
  'Collectables',
  'Toys',
  'Sports Equipment',
  'Sporting Goods',
];

export default function EditEvent() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<EditEventForm>({
    eventName: '',
    description: '',
    category: '',
    eventDateTime: '',
    eventStartDateTime: '',
    mustArriveByDateTime: '',
    tradeCutoffDate: '',
    selectedCity: null,
    venueAddress: '',
    maxParticipants: '',
    participantInitiationEnabled: true,
    status: 'active'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [citySearchInput, setCitySearchInput] = useState('');
  const [cityOptions, setCityOptions] = useState<CityOption[]>([]);
  const [citySearchLoading, setCitySearchLoading] = useState(false);
  const [citySearchTimeout, setCitySearchTimeout] = React.useState<NodeJS.Timeout | null>(null);

  const hasTradeCutoffPassed = (tradeCutoffDate?: string | null) => {
    if (!tradeCutoffDate) return false;
    const parsed = new Date(tradeCutoffDate);
    if (Number.isNaN(parsed.getTime())) return false;
    return Date.now() >= parsed.getTime();
  };

  const eventLocked = hasTradeCutoffPassed(formData.tradeCutoffDate);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Fetch event data
  useEffect(() => {
    if (!eventId || !getAuthUserId(user)) return;

    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${buildEventsApiUrl(`/api/events/${eventId}`)}`);
        
        if (!response.ok) {
          throw new Error('Failed to load event');
        }

        const data = await response.json();
        const event = data.event;

        // Check if user owns this event
        if (event.ownerID !== getAuthUserId(user)) {
          setError('You do not have permission to edit this event');
          return;
        }

        // Build city object
        const cityObj: CityOption = {
          city: event.listingCityName,
          state: event.listingStateName,
          stateAbbr: event.listingStateName?.substring(0, 2) || '',
          lat: event.coordinates?.lat || 0,
          lng: event.coordinates?.lng || 0
        };

        setFormData({
          eventName: event.eventName,
          description: event.description,
          category: event.category,
          eventDateTime: (event.eventDateTime || '').substring(0, 16), // Format for datetime input
          eventStartDateTime: (event.eventStartDateTime || event.eventDateTime || '').substring(0, 16),
          mustArriveByDateTime: (event.mustArriveByDateTime || event.eventDateTime || '').substring(0, 16),
          tradeCutoffDate: event.tradeCutoffDate ? event.tradeCutoffDate.substring(0, 16) : '',
          selectedCity: cityObj,
          venueAddress: event.venueAddress,
          maxParticipants: event.maxParticipants ? event.maxParticipants.toString() : '',
          participantInitiationEnabled: event.participantInitiationEnabled !== false,
          status: event.status || 'active'
        });

        setCitySearchInput(`${event.listingCityName}, ${event.listingStateName}`);
      } catch (err: any) {
        console.error('Error loading event:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, user]);

  const handleCitySearch = useCallback(
    async (query: string) => {
      if (citySearchTimeout) clearTimeout(citySearchTimeout);

      if (!query || query.trim().length < 2) {
        setCityOptions([]);
        return;
      }

      setCitySearchLoading(true);

      const timeout = setTimeout(async () => {
        try {
          const results = await searchCities(query);
          setCityOptions(results);
        } catch (err: any) {
          console.error('Search error:', err);
          setCityOptions([]);
        } finally {
          setCitySearchLoading(false);
        }
      }, 300);

      setCitySearchTimeout(timeout);
    },
    [citySearchTimeout]
  );

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.eventName.trim()) {
      setError('Event name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Event description is required');
      return;
    }

    if (!formData.category) {
      setError('Category is required');
      return;
    }

    if (!formData.eventDateTime) {
      setError('Event date and time are required');
      return;
    }

    if (!formData.selectedCity) {
      setError('Location is required');
      return;
    }

    if (!formData.venueAddress.trim()) {
      setError('Venue address is required');
      return;
    }

    const eventDate = new Date(formData.eventDateTime);

    if (formData.tradeCutoffDate) {
      const cutoffDate = new Date(formData.tradeCutoffDate);
      const hourBefore = new Date(eventDate.getTime() - 60 * 60 * 1000);

      if (cutoffDate >= hourBefore) {
        setError('Trade cutoff must be at least 1 hour before the event');
        return;
      }
    }

    if (formData.mustArriveByDateTime) {
      const mustArriveByDate = new Date(formData.mustArriveByDateTime);
      if (mustArriveByDate <= eventDate) {
        setError('Must arrive by date and time must be after the event date and time');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      
      const eventData = {
        eventName: formData.eventName,
        description: formData.description,
        category: formData.category,
        eventDateTime: formData.eventDateTime,
        eventStartDateTime: formData.eventDateTime,
        mustArriveByDateTime: formData.mustArriveByDateTime || '',
        tradeCutoffDate: formData.tradeCutoffDate || null,
        listingCityName: formData.selectedCity.city,
        listingStateName: formData.selectedCity.state,
        venueAddress: formData.venueAddress,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        participantInitiationEnabled: formData.participantInitiationEnabled,
        status: formData.status,
        ownerID: getAuthUserId(user)
      };
      
      console.log('🎯 Updating event with data:', eventData);
      
      const authUserId = getAuthUserId(user);
      const response = await fetch(`${buildEventsApiUrl(`/api/events/${eventId}`)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authUserId ? { 'x-user-id': authUserId } : {})
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Event updated:', result);
      
      setSuccess(true);
      
      // Redirect to dashboard after success
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const authUserId = getAuthUserId(user);
      const response = await fetch(`${buildEventsApiUrl(`/api/events/${eventId}`)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(authUserId ? { 'x-user-id': authUserId } : {})
        },
        body: JSON.stringify({ ownerID: authUserId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('✅ Event deleted');
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ textTransform: 'none' }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon /> Edit Event
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              ✅ Event updated successfully! Redirecting...
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {eventLocked && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Trade cutoff has passed. Event changes are locked.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Event Name */}
              <Grid item xs={12}>
                <TextField
                  label="Event Name"
                  fullWidth
                  required
                  value={formData.eventName}
                  onChange={(e) => handleInputChange('eventName', e.target.value)}
                  disabled={saving || eventLocked}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Event Description"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={saving || eventLocked}
                />
              </Grid>

              {/* Category & Status */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    disabled={saving || eventLocked}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={saving || eventLocked}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 2 }}>
                  📅 Event Schedule
                </Typography>
              </Grid>

              {/* Event Date & Time */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Event Date & Time"
                  type="datetime-local"
                  fullWidth
                  required
                  value={formData.eventDateTime}
                  onChange={(e) => handleInputChange('eventDateTime', e.target.value)}
                  disabled={saving || eventLocked}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Trade Cutoff Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Trade Cutoff Date"
                  type="datetime-local"
                  fullWidth
                  value={formData.tradeCutoffDate}
                  onChange={(e) => handleInputChange('tradeCutoffDate', e.target.value)}
                  disabled={saving || eventLocked}
                  InputLabelProps={{ shrink: true }}
                  helperText="Last time to add/modify items"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Must Arrive By"
                  type="datetime-local"
                  fullWidth
                  value={formData.mustArriveByDateTime}
                  onChange={(e) => handleInputChange('mustArriveByDateTime', e.target.value)}
                  disabled={saving || eventLocked}
                  InputLabelProps={{ shrink: true }}
                  helperText="This must be after the event date and time"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.participantInitiationEnabled} onChange={(e) => handleInputChange('participantInitiationEnabled', e.target.checked)} disabled={saving || eventLocked} />}
                  label="Allow participants to initiate eligible group trades"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', mt: 2 }}>
                  <LocationOnIcon sx={{ mr: 1 }} />
                  Event Location
                </Typography>
              </Grid>

              {/* City Search Autocomplete */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={cityOptions}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${option.city}, ${option.stateAbbr}`
                  }
                  value={formData.selectedCity}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue !== 'string') {
                      handleInputChange('selectedCity', newValue);
                    }
                  }}
                  inputValue={citySearchInput}
                  onInputChange={(event, newInputValue) => {
                    setCitySearchInput(newInputValue);
                    handleCitySearch(newInputValue);
                  }}
                  loading={citySearchLoading}
                  disabled={saving || eventLocked}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Event Location"
                      placeholder="e.g., 'New York' or 'Appleton, WI'"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {citySearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Box>
                          <Typography variant="body2">
                            {option.city}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {option.state} ({option.stateAbbr})
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                  noOptionsText={citySearchInput.length < 2 ? 'Type at least 2 characters' : 'No cities found'}
                  ListboxProps={{
                    sx: { maxHeight: 300 }
                  }}
                />
              </Grid>

              {/* Venue Address */}
              <Grid item xs={12}>
                <TextField
                  label="Venue Address"
                  fullWidth
                  required
                  value={formData.venueAddress}
                  onChange={(e) => handleInputChange('venueAddress', e.target.value)}
                  disabled={saving || eventLocked}
                  placeholder="123 Main St, Community Center, etc."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Max Participants (optional)"
                  type="number"
                  fullWidth
                  value={formData.maxParticipants}
                  onChange={(e) => handleInputChange('maxParticipants', e.target.value)}
                  disabled={saving || eventLocked}
                  placeholder="Leave empty for unlimited"
                />
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    disabled={saving}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    disabled={saving || eventLocked}
                  >
                    Delete Event
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || eventLocked || !formData.selectedCity}
                    sx={{
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      px: 4
                    }}
                  >
                    {saving ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>

          {eventId && getAuthUserId(user) && (
            <EventLocationsManager
              eventId={eventId}
              eventName={formData.eventName}
              userId={getAuthUserId(user) || ''}
              disabled={saving}
            />
          )}
        </Grid>

        {/* Right Sidebar - Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: '600' }}>
                ℹ️ Event Tips
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                    📝 Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    <strong>Active:</strong> Event is visible to others<br/>
                    <strong>Draft:</strong> Event is hidden from others
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: '600', mb: 1 }}>
                    ⏰ Past Events
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Events with dates in the past are automatically marked as "Past" and cannot accept new participants.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}






